import { KeyboardEvent, useEffect, useRef, useState } from "react"
import { Ball, Effect, Entity, GameField, GameState, Position2D, UseBreakoutFn } from "./types";
import levels from './levels'
import useBreakoutGPUPlugin from "./useBreakoutGPUPlugin";
import { detectCollision, rotateVector } from "./utils";
import clone from 'clone'
import { soundboard } from "../../hugo";
import { START_DELAY } from "./constants";

const breakoutMusic = soundboard.breakout(false);


const getEmptyGameState = (levelIndex: number = 0, lives: number = 5): GameState => ({
    lives,
    tick: 0,
    currentLevel: levelIndex,
    isPlaying: false,
    balls: clone(levels[levelIndex].balls),
    player: clone(levels[levelIndex].player),
    effects: clone(levels[levelIndex].effects),
    field: clone(levels[levelIndex].field),
    score: 0,
    controls: { left: false, right: false },
    notifications: [],
    luck: levels[levelIndex].luck,
})

const useBreakout: UseBreakoutFn = ({ tickRate = 12, hugoApi, onGameOver = () => { }, onWon = () => { } }) => {
    const [gameState, setGameState] = useState<GameState>(getEmptyGameState());
    const gameStateReference = useRef(gameState)
    const gpuPlugin = useBreakoutGPUPlugin({ gameStateRef: gameStateReference })

    useEffect(() => {
        gameStateReference.current = gameState
    }, [gameState])
    const start = (levelIndex: number = 0) => {
        setGameState(getEmptyGameState(levelIndex))
        play();
    }

    const play = () => {
        setGameState(current => ({ ...current, isPlaying: true }))
    }

    const pause = () => {
        setGameState(current => ({ ...current, isPlaying: false }))
    }

    const stop = () => {
        pause();
        reset();
    }

    const setTick = (to: number) => {
        setGameState(current => ({ ...current, tick: to }))
    }

    const reset = () => {
        setTick(0)
    }

    const nextLevel = () => {
        setGameState(currentState => ({
            ...currentState,
            currentLevel: currentState.currentLevel !== null ? currentState.currentLevel + 1 : 0
        }))
    }

    const render = (field: GameField) => {
        const renderField = [...field.map(row => [...row])];

        if (gameState.balls.length > 0) {
            gameState.balls.forEach((ball, index) => {
                const relativeBallPos = { x: Math.round(ball.position.x), y: Math.round(ball.position.y) }
                if (renderField[relativeBallPos.y] && typeof renderField[relativeBallPos.y][relativeBallPos.x] !== "undefined") {
                    renderField[relativeBallPos.y][relativeBallPos.x] = Entity.Ball
                }
            })


        }

        if (gameState.player) {
            for (let xOffset = gameState.player.position.x - gameState.player.width; xOffset <= gameState.player.position.x + gameState.player.width; xOffset++) {
                renderField[gameState.player.position.y][xOffset] = Entity.Player
            }
        }
        hugoApi.output(renderField, { plugins: [gpuPlugin], append: false, initalTick: gameState.tick },)
    }

    const hit = (at: Position2D) => {
        setGameState(currentGameState => {
            let newState = { ...currentGameState }
            // Lets hit all briks in a %4 +1 scheme
            const fromX = ((at.x + 1) - ((at.x - 1) % 4 + 1))
            const toX = fromX + 4

            let hitMultiplier = 1
            const possible = [Entity.Empty, Entity.BlockShield1, Entity.BlockShield2, Entity.BlockShield3, Entity.Buff, Entity.Debuff, Entity.Explosion, Entity.Wall]
            const rngReplacement = possible[Math.floor(Math.random() * possible.length)]
            for (let x = fromX; x < toX; x++) {
                if (newState.field && newState.field[at.y] && newState.field[at.y][x] && newState.field[at.y][x] !== Entity.Wall) {
                    switch (newState.field[at.y][x]) {
                        case Entity.BlockDefault:
                            soundboard.typewrite1()
                            newState.field[at.y][x] = Entity.Explosion
                            break;
                        case Entity.BlockShield1:
                            soundboard.typewrite2()
                            newState.field[at.y][x] = Entity.BlockDefault
                            break;
                        case Entity.BlockShield2:
                            soundboard.typewrite2()
                            newState.field[at.y][x] = Entity.BlockShield1
                            break;
                        case Entity.BlockShield3:
                            soundboard.typewrite2()
                            newState.field[at.y][x] = Entity.BlockShield2
                            break;
                        case Entity.BlockRandom:
                            soundboard.success()
                            newState.field[at.y][x] = rngReplacement
                            break;

                    }
                    newState.score += Math.ceil(((newState.currentLevel || 1) * hitMultiplier * newState.field[at.y][x]) / 4)
                }
            }
            return newState
        })
    }

    const isFieldClear = (field: GameField): boolean => {
        const blockEntities = [
            Entity.BlockDefault,
            Entity.BlockShield1,
            Entity.BlockShield2,
            Entity.BlockShield3,
            Entity.BlockRandom,
        ]

        if (gameStateReference.current.field !== null) {
            return gameStateReference.current.field.every(row => row.every(entity => {
                return !blockEntities.includes(entity)
            }))
        }

        return false
    }

    const hurt = (causingBallIndex: number) => {
        setGameState(currentGameState => {
            const newState = { ...currentGameState }
            if (newState.currentLevel === null) return currentGameState

            // Remove Ball
            newState.balls.splice(causingBallIndex, 1)

            // No balls left
            if (newState.balls.length < 1) {
                if (newState.lives > 1) {
                    soundboard.error()
                    newState.lives--
                    newState.balls = clone(levels[newState.currentLevel].balls)
                    newState.player = clone(levels[newState.currentLevel].player)
                    newState.effects = clone(levels[newState.currentLevel].effects)
                    newState.notifications = []
                    newState.tick = 0

                } else {
                    soundboard.error()
                    onGameOver(newState.score)
                    newState.isPlaying = false
                }
            }

            return newState
        })
    }

    const addPlayerWidth = (val: number) => {
        setGameState((currentGameState) => {
            if (!currentGameState.player) return currentGameState
            let newWidth = currentGameState.player.width + val
            if (newWidth < 1) newWidth = 1
            if (newWidth > 5) newWidth = 5

            return {
                ...currentGameState,
                player: {
                    ...currentGameState.player,
                    width: newWidth
                }
            }
        })
    }
    const addPlayerSpeed = (val: number) => {
        setGameState((currentGameState) => {
            if (!currentGameState.player) return currentGameState
            let newSpeed = currentGameState.player.speed + val
            if (newSpeed < 1) newSpeed = 1
            if (newSpeed > 10) newSpeed = 10

            return {
                ...currentGameState,
                player: {
                    ...currentGameState.player,
                    speed: newSpeed
                }
            }
        })
    }

    const addLuck = (val: number) => {
        setGameState((currentGameState) => {
            if (!currentGameState.player) return currentGameState
            let newLuck = currentGameState.luck + val
            if (newLuck < 1) newLuck = 1
            if (newLuck > 20) newLuck = 20

            return {
                ...currentGameState,
                luck: newLuck,
            }
        })
    }

    const multiplayBallSpeed = (val: number) => {
        setGameState(currentGameState => {
            return {
                ...currentGameState,
                balls: currentGameState.balls.map(ball => ({
                    ...ball, velocity: {
                        x: ball.velocity.x * val,
                        y: ball.velocity.y * val,
                    }
                }))
            }
        })
    }

    const doubleBalls = () => {
        setGameState(currentGameState => {
            let addBalls: Ball[] = [...currentGameState.balls]
            currentGameState.balls.forEach(ball => {
                addBalls.push({
                    ...ball, velocity: {
                        y: ball.velocity.y,
                        x: ball.velocity.x * -1
                    }
                })
            })

            if (addBalls.length > 10) {
                addBalls = addBalls.slice(0, 10)
            }

            return {
                ...currentGameState,
                balls: addBalls,
            }
        })
    }

    const addNotification = (message: string, duration: number = 60) => {
        setGameState(currentGameState => {
            return {
                ...currentGameState,
                notifications: [
                    ...currentGameState.notifications,
                    {
                        message,
                        startTick: currentGameState.tick,
                        endTick: currentGameState.tick + duration,
                    }
                ]
            }
        })
    }
    const addEffect = (effect: Effect) => {
        setGameState(currentGameState => {
            if (currentGameState.effects.includes(effect)) return currentGameState
            return { ...currentGameState, effects: [...currentGameState.effects, effect] }
        })
    }

    const addScore = (val: number) => {
        setGameState(currentGameState => ({ ...currentGameState, score: currentGameState.score + val }))
    }

    const addRandomDebuff = (retry: number = 0): void => {
        const rng = Math.max(Math.round(Math.random() * 8), 1)
        if (retry > 3) return
        switch (rng) {
            case 1:
                soundboard.error()
                addNotification('Hurry Up!')
                multiplayBallSpeed(1.5)
                break;
            case 2:
                soundboard.error()
                addNotification('Shrink')
                addPlayerWidth(-1)
                break;
            case 3:
                soundboard.error()
                addNotification('Speed down!')
                addPlayerSpeed(-1)
                break;
            case 4:
                soundboard.error()
                addNotification('Luck down!')
                addLuck(-1)
                break;
            case 5:
                setGameState(cg => {
                    if (cg.player && cg.player.position.y > 12) {
                        addNotification('Getting closer!')
                        soundboard.error()
                        return { ...cg, player: { ...cg.player, position: { x: cg.player.position.x, y: cg.player.position.y - 1 } } }
                    } else {
                        addRandomDebuff(retry + 1)
                    }
                    return cg
                })
                break;

            default:
                const add = Math.max(Math.floor(Math.random() * 5), 1) * -50
                addNotification('-' + Math.abs(add))
                soundboard.error()
                addScore(add)
                break;
        }
    }

    const addRandomBuff = (retry: number = 0): void => {
        const rng = Math.max(Math.round(Math.random() * 20), 1)
        if (retry > 3) return
        switch (rng) {
            case 1:
                if (gameStateReference.current.effects.includes(Effect.BallDuplicatorPlatform)) {
                    return addRandomBuff(retry + 1)
                }
                soundboard.boot()
                addNotification('Duplicator Platform unlocked')
                addEffect(Effect.BallDuplicatorPlatform)
                break;
            case 2:
                if (gameStateReference.current.effects.includes(Effect.PiercingBall)) {
                    return addRandomBuff(retry + 1)
                }
                soundboard.boot()
                addNotification('Piercing Balls unlocked')
                addEffect(Effect.PiercingBall)
                break;
            case 3:
                soundboard.success()
                addNotification('Double Up')
                doubleBalls()
                break;
            case 5:
                soundboard.success()
                addNotification('Slow Down!')
                multiplayBallSpeed(0.75)
                break;
            case 6:
                soundboard.success()
                addNotification('Growth')
                addPlayerWidth(1)
                break;
            case 8:
                soundboard.success()
                addNotification('Speed up!')
                addPlayerSpeed(1)
                break;
            case 10:
                soundboard.success()
                addNotification('Luck +1!')
                addLuck(1)
                break;
            case 11:
                soundboard.success()
                addNotification('Luck +2!')
                addLuck(2)
                break;
            case 15:
                setGameState(cg => {
                    if (cg.player && cg.player.position.y < 14) {
                        addNotification('Looks live some Gravity!')
                        soundboard.error()
                        return { ...cg, player: { ...cg.player, position: { x: cg.player.position.x, y: 14 } } }
                    } else {
                        addRandomBuff(retry + 1)
                    }
                    return cg
                })
                break;
            case 15:
                setGameState(cg => {
                    addNotification('1 UP!')
                        soundboard.complete()
                        return { ...cg, lives: cg.lives + 1 }
                })
                break;

            default:
                const add = Math.max(Math.floor(Math.random() * 5), 1) * 100
                soundboard.success()
                addNotification('+' + add)
                addScore(add)
                break;
        }
    }

    // Main Tick Interval
    useEffect(() => {
        const handleTick = () => {
            setGameState(current => ({ ...current, tick: current.tick + 1 }))
        }

        if (gameState.isPlaying) {
            const tickInterval = setInterval(handleTick, (1 / tickRate * 1000))
            return () => { clearInterval(tickInterval) }
        }
    }, [gameState.isPlaying, tickRate])

    // Music
    useEffect(() => {
        if (gameState.isPlaying) {
            breakoutMusic.pause()
            breakoutMusic.currentTime = 0
            if (!soundboard.isPlaying(breakoutMusic)) {
                breakoutMusic.play();
            }
        } else {
            if (soundboard.isPlaying(breakoutMusic)) { breakoutMusic.pause() }

        }
        return () => {
            if (soundboard.isPlaying(breakoutMusic)) { breakoutMusic.pause() }
        }

    }, [gameState.currentLevel, gameState.isPlaying])


    // levelChange
    useEffect(() => {
        if (gameState.currentLevel === null) return

        if (gameState.currentLevel > levels.length - 1) {
            setGameState(c => ({ ...c, isPlaying: false }))
            setTimeout(() => {
                breakoutMusic.pause()
                soundboard.complete()
                onWon(gameStateReference.current.score)
            }, 100)
            return;
        }

        setGameState(currentGameState => {
            if (gameState.currentLevel === null) return currentGameState

            if (gameState.currentLevel > 0) {

                soundboard.complete()
            }
            const newState = clone(currentGameState)
            newState.balls = clone(levels[gameState.currentLevel].balls)
            newState.player = clone(levels[gameState.currentLevel].player)
            newState.effects = clone(levels[gameState.currentLevel].effects)
            newState.controls = { left: false, right: false }
            newState.luck = levels[gameState.currentLevel].luck
            newState.field = clone(levels[gameState.currentLevel].field)
            newState.notifications = []
            newState.tick = 0;


            return newState
        })
    }, [gameState.currentLevel])

    // Game Mechanics
    useEffect(() => {
        // Update gamestate
        setGameState(currentGameState => {
            // Dont do anything on pause :)
            if (!currentGameState.isPlaying) return currentGameState
            const newState = { ...currentGameState }
            if (newState.tick < START_DELAY && newState.tick > 1) return currentGameState

            // movement
            if (newState.player) {
                const speed = (newState.player.speed > 5 ? 5 : newState.player.speed) + 5;
                const dist = ((5 - newState.player.speed) * -1) < 1 ? 1 : (5 - newState.player.speed) * -1

                if (newState.tick % (11 - speed) === 0) {
                    let newX = newState.player.position.x
                    if (newState.controls.left) {
                        newX -= dist
                        if (newX < 0 + newState.player.width) {
                            newX = newState.player.width
                        }
                    }
                    if (newState.controls.right) {
                        newX += dist
                        if (newX > 33 - newState.player.width) {
                            newX = 33 - newState.player.width
                        }
                    }
                    newState.player.position.x = newX
                }
            }


            // Calculate Balls
            if (newState.balls.length > 0) {
                const additionalBalls: Ball[] = []
                newState.balls = newState.balls.map((currentBall: Ball, index) => {
                    if (newState.field === null || newState.player === null) return currentBall
                    let newBall: Ball = { ...currentBall }
                    let applyVelocity = { ...newBall.velocity }


                    // Collision Detection
                    const currentRPosition: Position2D = { x: Math.round(newBall.position.x), y: Math.round(newBall.position.y) }
                    const targetRPosition: Position2D = { x: Math.round(newBall.position.x + applyVelocity.x), y: Math.round(newBall.position.y + applyVelocity.y) }
                    let lastHitEntity: Entity | false


                    // Collides Top or Bottom
                    if (lastHitEntity = detectCollision({ x: currentRPosition.x, y: targetRPosition.y }, newState.field)) {
                        if (lastHitEntity !== Entity.Buff && lastHitEntity !== Entity.Debuff && lastHitEntity !== Entity.Explosion) {
                            hit({ x: currentRPosition.x, y: targetRPosition.y })
                            if (lastHitEntity === Entity.Wall || !newState.effects.includes(Effect.PiercingBall)) {
                                applyVelocity.y *= -1
                            }
                        }
                    }
                    // Collides Left or Right
                    if (lastHitEntity = detectCollision({ x: targetRPosition.x, y: currentRPosition.y }, newState.field)) {
                        if (lastHitEntity !== Entity.Buff && lastHitEntity !== Entity.Debuff &&lastHitEntity !== Entity.Explosion) {
                            hit({ x: targetRPosition.x, y: currentRPosition.y })
                            if (lastHitEntity === Entity.Wall || !newState.effects.includes(Effect.PiercingBall)) {
                                applyVelocity.x *= -1
                            }
                        }

                    }



                    // Ball hits Player
                    if (
                        Math.ceil(newBall.position.y + applyVelocity.y) == newState.player.position.y
                        && targetRPosition.x >= newState.player.position.x - newState.player.width
                        && targetRPosition.x <= newState.player.position.x + newState.player.width
                    ) {
                        applyVelocity.y *= -1

                        const offset = newState.player.position.x - targetRPosition.x
                        const offsetAngles = [0, 3, 5, 8, 12]

                        applyVelocity = rotateVector(applyVelocity, (offsetAngles[Math.abs(Math.round(offset))] || 0) * offset)

                        if (newState.effects.includes(Effect.BallDuplicatorPlatform) && newState.balls.length < 10) {
                            additionalBalls.push({
                                ...clone(currentBall),
                                velocity: { x: applyVelocity.x * -1, y: applyVelocity.y },
                                position: { x: currentBall.position.x, y: Math.floor(currentBall.position.y) }
                            })

                        }
                    }

                    // Ball gets out of field
                    if (targetRPosition.x < 0 || targetRPosition.x > 34) {
                        applyVelocity.x *= -1
                    }
                    if (targetRPosition.y < 0) {
                        applyVelocity.y *= -1
                    }
                    if (targetRPosition.y > 14) {
                        applyVelocity.y *= -1
                        hurt(index)
                        return newBall;
                    }

                    applyVelocity.x = Math.round(applyVelocity.x * 10) / 10
                    applyVelocity.y = Math.round(applyVelocity.y * 10) / 10
                    if (applyVelocity.y < 0.2 && applyVelocity.y > 0) applyVelocity.y = 0.2
                    if (applyVelocity.y > -0.2 && applyVelocity.y <= 0) applyVelocity.y = -0.2

                    let applyPosition = {
                        x: Math.round((newBall.position.x + applyVelocity.x) * 10) / 10,
                        y: Math.round((newBall.position.y + applyVelocity.y) * 10) / 10,
                    }

                    if (newState.tick > START_DELAY) {
                        newBall.position = applyPosition
                        newBall.velocity = applyVelocity
                    }

                    return newBall
                })

                if (additionalBalls.length > 0) {
                    newState.balls = [...newState.balls, ...additionalBalls]
                }
            }

            // Collect Items
            if (newState.field) {
                newState.field.forEach((row, y) => row.forEach((entity, x) => {
                    if (entity === Entity.Buff) {
                        if (newState.field && y === newState.player?.position.y) {
                            if (newState.player &&
                                x >= newState.player.position.x - newState.player.width &&
                                x <= newState.player.position.x + newState.player.width
                            ) {
                                addRandomBuff();
                            }
                            newState.field[y][x] = Entity.Empty
                        }
                    }
                    if (entity === Entity.Debuff) {
                        if (newState.field && y === newState.player?.position.y) {
                            if (newState.player &&
                                x >= newState.player.position.x - newState.player.width &&
                                x <= newState.player.position.x + newState.player.width
                            ) {
                                addRandomDebuff();
                            }
                            newState.field[y][x] = Entity.Empty
                        }
                    }
                }))
            }

            // Calculate Items
            if (newState.field && newState.tick % 12 === 0) {
                const addBuffs: Position2D[] = []
                const addDebuffs: Position2D[] = []
                newState.field.forEach((row, y) => row.forEach((entity, x) => {
                    if (entity === Entity.Buff) {
                        if (newState.field && newState.field[y + 1] && newState.field[y + 1][x] === Entity.Empty) {
                            addBuffs.push({ x, y: y + 1 })
                            newState.field[y][x] = Entity.Empty
                        }
                    }
                    if (entity === Entity.Debuff) {
                        if (newState.field && newState.field[y + 1] && newState.field[y + 1][x] === Entity.Empty) {
                            addDebuffs.push({ x, y: y + 1 })
                            newState.field[y][x] = Entity.Empty
                        }
                    }
                }))

                addBuffs.forEach(itemPosition => {
                    if (newState.field && newState.field[itemPosition.y] && newState.field[itemPosition.y][itemPosition.x] === Entity.Empty) {
                        newState.field[itemPosition.y][itemPosition.x] = Entity.Buff
                    }
                })
                addDebuffs.forEach(itemPosition => {
                    if (newState.field && newState.field[itemPosition.y] && newState.field[itemPosition.y][itemPosition.x] === Entity.Empty) {
                        newState.field[itemPosition.y][itemPosition.x] = Entity.Debuff
                    }
                })
            }

            // Render Gamefield
            if (newState.isPlaying && newState.field !== null) {
                render(newState.field)
                // Remove Explosions and add Items
                newState.field = newState.field.map(row => row.map(entity => {
                    if (entity === Entity.Explosion) {
                        const goodOrBad = Math.random() > (0.4 - (newState.luck / 100)) ? Entity.Buff : Entity.Debuff
                        return Math.random() > (1 - (newState.luck / 20)) ? goodOrBad : Entity.Empty
                    }
                    return entity
                }))
            }

            return newState
        });




    }, [gameState.tick])



    // Check if field is cleared
    useEffect(() => {
        if (gameState.field !== null && isFieldClear(gameState.field)) {
            nextLevel()
        }

    }, [gameState.field])



    // Bind Keys
    useEffect(() => {

        const onKeyPress = (event: Event) => {
            const keyEvent = event as unknown as KeyboardEvent<Document>
            if (keyEvent.key === "Escape") {
                setGameState(currentGameState => ({
                    ...currentGameState,
                    isPlaying: !currentGameState.isPlaying,
                }))
            }
            if (keyEvent.key === "ArrowLeft" || keyEvent.key === "a") {
                setGameState(currentGameState => ({
                    ...currentGameState,
                    controls: { ...currentGameState.controls, left: true }
                }))
            }
            if (keyEvent.key === "ArrowRight" || keyEvent.key === "d") {
                setGameState(currentGameState => ({
                    ...currentGameState,
                    controls: { ...currentGameState.controls, right: true }
                }))
            }
        }

        const onKeyRelease = (event: Event) => {
            const keyEvent = event as unknown as KeyboardEvent<Document>
            if (keyEvent.key === "ArrowLeft" || keyEvent.key === "a") {
                setGameState(currentGameState => ({
                    ...currentGameState,
                    controls: { ...currentGameState.controls, left: false }
                }))
            }
            if (keyEvent.key === "ArrowRight" || keyEvent.key === "d") {
                setGameState(currentGameState => ({
                    ...currentGameState,
                    controls: { ...currentGameState.controls, right: false }
                }))
            }
        }
        const onKeyDown = (event: Event) => {
            const keyDownEvent = event as unknown as KeyboardEvent<Document>

        }

        if (document.activeElement === document.body) {
            document.addEventListener('keydown', onKeyPress)
            document.addEventListener('keyup', onKeyRelease)
        }

        return () => {
            document.removeEventListener('keydown', onKeyPress)
            document.removeEventListener('keyup', onKeyRelease)
        }

    }, [document.activeElement])


    return [
        {
            start,
            play,
            pause,
            stop,
            reset,
            setTick,
        },
        gameStateReference.current
    ]
}

export default useBreakout
